import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearch } from "@tanstack/react-router";
import { BarChart3, Plus } from "lucide-react";
import { broadcastAction, listBroadcasts, listSegments } from "@/lib/emailApi";
import { BRANDS } from "@/lib/brands";
import { formatInt, formatWhen } from "@/lib/format";
import type { Broadcast } from "@/lib/types";
import { BrandMark } from "@/components/BrandMark";
import { Empty, PageHeader } from "@/components/PageHeader";
import { useToast } from "@/components/Toaster";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { useWarmup } from "./WarmupPage";
import { AttendeeBadge, LabelChip } from "./EmailLabel";
import { SendingCard } from "./SendingControls";

export const STATUS: Record<Broadcast["status"], { label: string; variant: "success" | "warning" | "destructive" | "muted" | "default" }> = {
  draft: { label: "draft", variant: "muted" },
  scheduled: { label: "scheduled", variant: "default" },
  queued: { label: "queued", variant: "default" },
  sending: { label: "sending", variant: "warning" },
  paused: { label: "paused", variant: "warning" },
  completed: { label: "sent", variant: "success" },
  cancelled: { label: "cancelled", variant: "muted" },
  imported: { label: "imported", variant: "muted" },
};

export function BroadcastsPage() {
  // ⌘K "Pause all email sending" lands here with ?pause=true and opens the pause confirm.
  const search = useSearch({ from: "/email/broadcasts" });
  const [brand, setBrand] = useState<number | "">("");
  const [status, setStatus] = useState("");
  const q = useQuery({ queryKey: ["broadcasts", brand, status], queryFn: () => listBroadcasts({ brand_id: brand || undefined, status: status || undefined }) });
  const segs = useQuery({ queryKey: ["segments"], queryFn: () => listSegments({ include_archived: true }) });
  const segName = (id: number | null) => (id ? (segs.data?.find((s) => s.id === id)?.name ?? `segment #${id}`) : "custom rules");
  const qc = useQueryClient();
  const toast = useToast();
  const act = useMutation({
    mutationFn: ({ id, action }: { id: number; action: "pause" | "resume" | "cancel" }) => broadcastAction(id, action),
    onSuccess: (_r, v) => {
      void qc.invalidateQueries({ queryKey: ["broadcasts"] });
      toast(v.action === "pause" ? "Paused" : v.action === "resume" ? "Resumed" : "Cancelled");
    },
    onError: (e) => toast(e.message, "error"),
  });
  const rows = [...(q.data ?? [])].sort((a, b) => b.id - a.id);
  return (
    <div className="mx-auto max-w-[1300px]">
      <PageHeader
        title="Broadcasts"
        actions={
          <>
            <Select className="w-auto" value={brand} onChange={(e) => setBrand(e.target.value ? Number(e.target.value) : "")} aria-label="Brand">
              <option value="">All brands</option>
              {BRANDS.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.short}
                </option>
              ))}
            </Select>
            <Select className="w-auto" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
              <option value="">Any status</option>
              {Object.entries(STATUS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v.label}
                </option>
              ))}
            </Select>
            <Button size="sm" asChild>
              <Link to="/email/broadcasts/$broadcastId" params={{ broadcastId: "new" }}>
                <Plus /> New broadcast
              </Link>
            </Button>
          </>
        }
      >
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Sent broadcasts' results are under <Link to="/email/reports" search={{ kind: "broadcast" }} className="text-[var(--color-primary)] hover:underline">Reports</Link>. Times are Mountain Time.
        </p>
        <WarmupLine />
      </PageHeader>
      {/* The emergency stop for all email, always here (the warm-up page that had it is out of the menu). */}
      <div className="mb-3">
        <SendingCard askPause={!!search.pause} />
      </div>
      <Card className="overflow-x-auto">
        {q.isLoading && <Skeleton className="m-3 h-32" />}
        {q.error && <p className="px-4 py-3 text-sm text-[var(--color-destructive)]">Couldn't load broadcasts: {q.error.message}</p>}
        {!q.isLoading && !q.error && rows.length === 0 && <Empty title="No broadcasts here yet.">Press New broadcast to write one, preview it light and dark, and schedule it.</Empty>}
        {rows.length > 0 && (
          <Table>
            <THead>
              <tr>
                <TH>Broadcast</TH>
                <TH>Brand</TH>
                <TH>To</TH>
                <TH>Status</TH>
                <TH>When</TH>
                <TH className="text-right">Recipients</TH>
                <TH />
              </tr>
            </THead>
            <tbody>
              {rows.map((b) => (
                <TR key={b.id}>
                  <TD className="max-w-[18rem]">
                    <Link to="/email/broadcasts/$broadcastId" params={{ broadcastId: String(b.id) }} className="block truncate font-medium text-[var(--color-primary)] hover:underline">
                      {b.name}
                    </Link>
                    <span className="mt-0.5 flex flex-wrap gap-1">
                      {b.attendee_email && <AttendeeBadge />}
                      <LabelChip label={b.label} />
                    </span>
                  </TD>
                  <TD>
                    <BrandMark id={b.brand_id} />
                  </TD>
                  <TD className="max-w-[14rem] truncate">{segName(b.segment_id)}</TD>
                  <TD>
                    <Badge variant={STATUS[b.status]?.variant ?? "muted"}>{STATUS[b.status]?.label ?? b.status}</Badge>
                  </TD>
                  <TD className="whitespace-nowrap">{b.completed_at ? formatWhen(b.completed_at) : b.started_at ? formatWhen(b.started_at) : b.scheduled_at ? formatWhen(b.scheduled_at) : "—"}</TD>
                  <TD className="num text-right">{b.recipients_count != null ? formatInt(b.recipients_count) : b.waiting_in_outbox ? `${formatInt(b.waiting_in_outbox)} waiting` : "—"}</TD>
                  <TD className="whitespace-nowrap text-right">
                    {b.report_id ? (
                      <Button size="sm" variant="ghost" asChild>
                        <Link to="/email/reports/$reportId" params={{ reportId: String(b.report_id) }}>
                          <BarChart3 /> Report
                        </Link>
                      </Button>
                    ) : null}
                    {(b.status === "scheduled" || b.status === "sending" || b.status === "queued") && (
                      <Button size="sm" variant="ghost" onClick={() => act.mutate({ id: b.id, action: "pause" })}>
                        Pause
                      </Button>
                    )}
                    {b.status === "paused" && (
                      <Button size="sm" variant="ghost" onClick={() => act.mutate({ id: b.id, action: "resume" })}>
                        Resume
                      </Button>
                    )}
                    {["scheduled", "paused", "queued"].includes(b.status) && (
                      <Button size="sm" variant="ghost" className="text-[var(--color-destructive)]" onClick={() => window.confirm(`Cancel "${b.name}"? People already sent to stay sent; nobody else gets it.`) && act.mutate({ id: b.id, action: "cancel" })}>
                        Cancel
                      </Button>
                    )}
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}

/** One line about the sending IP's warm-up, linking to its page; nothing once it's finished. */
function WarmupLine() {
  const w = useWarmup().data;
  if (!w || !w.active) return null;
  return (
    <p className="mt-1 text-sm">
      <Link to="/email/warmup" className="text-[var(--color-primary)] hover:underline">
        IP warm-up, stage {w.stage}
      </Link>
      <span className="text-[var(--color-muted-foreground)]">
        : {formatInt(w.sent_today)} of {formatInt(w.cap_today)} sent today
        {w.current && w.current.engaged_days > 0 ? `, to people engaged in the last ${w.current.engaged_days} days` : ""}.
      </span>
      {!w.healthy && <span className="text-[var(--color-danger-fg)]"> Bounces or complaints are too high.</span>}
    </p>
  );
}
